import React, { useEffect, useState, useRef } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import SearchIcon from "@mui/icons-material/Search";

import "./styles.css";
import useGoogleMaps from "../../../../../hooks/useGoogleMaps";
import { generateDestinationListingUrl } from "../../../../../utils/formatting/distinations-listing/url.utils";

// Helper function to decide if an address component is a city type
function isCityType(type) {
  const cityTypes = [
    "administrative_area_level_3",
    "locality",
    "political",
    "neighborhood",
    "sublocality",
    "sublocality_level_1",
    "sublocality_level_2",
    "sublocality_level_3",
    "sublocality_level_4",
    "sublocality_level_5",
    "establishment",
  ];
  return cityTypes.includes(type);
}

const LocationSearch = ({ searchCriteria, params, setSuggestions, suggestions, isFocused, setIsFocused }) => {
  const navigate = useNavigate();
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  // // Use the custom hook to ensure Google Maps is loaded (with the "places" library)
  const googleMapsLoaded = useGoogleMaps(process.env.REACT_APP_GOOGLE_API_KEY, ["places"]);

  // Destructure location and propertyDetails from params
  const { location, propertyDetails } = searchCriteria;

  // Compute the label (if a property is selected, show city and country)
  let label = location?.label;
  if (propertyDetails?.propertyId) {
    label = `${location.city}, ${location.country}`;
  }

  // Use a ref to hold our AutocompleteService instance
  const autocompleteServiceRef = useRef(null);
  useEffect(() => {
    if (googleMapsLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
  }, [googleMapsLoaded]);

  // Fetch predictions based on the query using AutocompleteService
  const fetchPlaces = (query) => {
    if (!query) {
      setSuggestions([]);
      return;
    }
    if (!autocompleteServiceRef.current) {
      console.error("AutocompleteService is not available.");
      return;
    }
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: query,
        componentRestrictions: { country: "lk" },
        types: ["(regions)"],
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const places = predictions.map((prediction) => ({
            _id: prediction.place_id,
            label: prediction.description,
          }));
          setSuggestions(places);
        } else {
          setSuggestions([]);
        }
      }
    );
  };

  // When the location label changes, refetch the places (if available)
  useEffect(() => {
    if (googleMapsLoaded && location.label) {
      fetchPlaces(location.label);
    }
  }, [location.label, googleMapsLoaded]);

  // Handle a new selection
  const onChange = (selectedOption) => {
    if (selectedOption) {
      fetchPlaceDetails(selectedOption.label)
        .then((placeDetails) => {
          // Create updated params using the new search criteria

          const updatedParams = {
            ...params,
            destination: selectedOption.label,
          };

          // Generate the URL and navigate to it
          const url = generateDestinationListingUrl({
            searchCriteria: {
              ...searchCriteria,
              location: {
                ...location,
                label: selectedOption.label,
                city: placeDetails.city,
                district: placeDetails.district,
                province: placeDetails.province,
                country: placeDetails.country,
                latitude: placeDetails.latitude,
                longitude: placeDetails.longitude,
              },
            },
            filters: { propertyType: updatedParams.propertyType },
          });
          navigate(url);
        })
        .catch((error) => {
          console.error("Error fetching place details:", error);
        });
      setSelectedSuggestion(selectedOption);
      setIsFocused(false);
    }
  };

  // Use the Geocoder service to get detailed place information
  const fetchPlaceDetails = (label) => {
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps) {
        reject(new Error("Google Maps API is not available"));
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: label }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results && results[0]) {
          const addressComponents = results[0].address_components;
          const geometry = results[0].geometry;

          let city = null;
          let district = null;
          let province = null;
          let country = null;
          let latitude = null;
          let longitude = null;

          // If we lack either address components or geometry, resolve with empty values.
          if (!addressComponents || !geometry) {
            resolve({ city, district, province, country, latitude, longitude });
            return;
          }

          // Parse the address components for the details we need
          addressComponents.forEach((component) => {
            const componentType = component.types[0];

            if (isCityType(componentType)) {
              city = component.long_name;
            }
            if (componentType === "administrative_area_level_2") {
              district = component.long_name;
            }
            if (componentType === "administrative_area_level_1") {
              province = component.long_name.replace(" Province", "");
            }
            if (componentType === "country") {
              country = component.long_name;
            }
          });

          latitude = geometry.location.lat();
          longitude = geometry.location.lng();

          // Override if the label suggests a District or Province search
          if (label.includes("District")) {
            city = null;
          }
          if (label.includes("Province")) {
            city = null;
            district = null;
          }

          resolve({ city, district, province, country, latitude, longitude });
        } else {
          reject(new Error("Geocode was not successful"));
        }
      });
    });
  };

  // If the API is not yet loaded, show a loading placeholder.
  if (!googleMapsLoaded) {
    return <div>Loading map services…</div>;
  }

  // Custom dropdown indicator to show the search icon
  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <SearchIcon style={{ color: "#757575", fontSize: 24 }} />
      </components.DropdownIndicator>
    );
  };

  // Custom styles for the react-select component; note the use of window.innerWidth
  const customStyles = {
    control: (provided) => ({
      ...provided,
      display: "flex",
      inset: "0",
      boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
      flexDirection: "row",
      fontSize: "16px",
      fontWeight: "400",
      border: "black",
      BorderColor: "black",
      height: "35px",
      padding: "0",
      margin: "0",
      width: window.innerWidth < 991 ? "100%" : "300px",
      "&:hover": {
        border: "none",
        BorderColor: "none",
      },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 0 0 8px",
      position: "absolute",
      left: "0",
    }),
    valueContainer: (provided) => ({
      ...provided,
      marginLeft: "34px",
    }),
    singleValue: (provided) => ({
      ...provided,
      marginLeft: "0",
      alignItems: "center",
      paddingLeft: "8px",
      fontSize: "16px",
      fontWeight: "500",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
      display: "flex",
      alignItems: "center",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "white",
      display: window.innerWidth > 991 && suggestions.length ? "block" : "none",
      fontSize: "16px",
      fontWeight: "400",
      high: "100%",
      width: window.innerWidth < 991 ? "100%" : "300px",
      margin: "0",
      border: "1px solid #e0e0e0",
    }),
  };

  return (
    <div className="places-search-bar">
      <Select
        options={suggestions.map((suggestion) => ({
          value: suggestion._id,
          label: suggestion.label,
        }))}
        onFocus={() => setIsFocused(true)}
        onInputChange={fetchPlaces}
        onChange={onChange}
        placeholder="Search on map"
        isClearable={true}
        styles={customStyles}
        className="places-search-selctor"
        components={{ DropdownIndicator }}
        value={selectedSuggestion || suggestions.find((suggestion) => suggestion.label === label)}
      />
      {window.innerWidth < 991 && isFocused && (
        <div className="suggestions-list-mobile">
          {suggestions.map((suggestion) => (
            <Button
              key={suggestion._id}
              className="suggestions-value-mobile"
              onTouchStart={() => {
                // Trigger the same change handler for mobile selection
                onChange({ value: suggestion._id, label: suggestion.label });
                setSelectedSuggestion({
                  value: suggestion._id,
                  label: suggestion.label,
                });
              }}
            >
              {suggestion.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
